import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Achievement } from '../../utils/achievements';

function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return n.toString();
}

function getHint(achievement: Achievement): string | null {
  if (achievement.progress == null || achievement.target == null) return null;
  const remaining = achievement.target - achievement.progress;
  if (remaining <= 0) return null;

  const map: Record<string, string> = {
    'first-wings': `Log ${remaining} more flight to unlock`,
    'globe-trotter': `Visit ${remaining} more airport${remaining > 1 ? 's' : ''} to unlock`,
    'century-club': `Log ${remaining} more flight${remaining > 1 ? 's' : ''} to unlock`,
    'long-hauler': `Fly a route over 3,000 nm to unlock`,
    'jet-setter': `Fly ${formatCompact(remaining)} more nm to unlock`,
    'million-miler': `Fly ${formatCompact(remaining)} more nm to unlock`,
    'city-hopper': `Visit ${remaining} more airport${remaining > 1 ? 's' : ''} in a single month`,
    'world-explorer': `Visit ${remaining} more countr${remaining > 1 ? 'ies' : 'y'} to unlock`,
    'social-butterfly': `Add ${remaining} more friend${remaining > 1 ? 's' : ''} to unlock`,
    'crew-recruiter': `Refer ${remaining} more friend${remaining > 1 ? 's' : ''} to unlock`,
    'crew-captain': `Refer ${remaining} more friend${remaining > 1 ? 's' : ''} to unlock`,
    'crew-ambassador': `Refer ${remaining} more friend${remaining > 1 ? 's' : ''} to unlock lifetime premium`,
  };
  return map[achievement.id] ?? `${formatCompact(remaining)} more to go`;
}

interface AchievementDetailProps {
  achievement: Achievement;
  onDismiss: () => void;
}

export function AchievementDetail({ achievement, onDismiss }: AchievementDetailProps) {
  const [leaving, setLeaving] = useState(false);
  const hint = getHint(achievement);
  const ratio = achievement.progress != null && achievement.target
    ? Math.min(achievement.progress / achievement.target, 1)
    : 0;
  const pct = Math.round(ratio * 100);

  useEffect(() => {
    if (!leaving) return;
    const timer = setTimeout(onDismiss, 250);
    return () => clearTimeout(timer);
  }, [leaving, onDismiss]);

  const dismiss = () => setLeaving(true);

  return createPortal(
    <div
      className={`fixed inset-0 z-[120] flex items-end justify-center transition-opacity duration-250 ${
        leaving ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" onClick={dismiss} />

      {/* Sheet */}
      <div
        className={`relative w-full max-w-[400px] bg-slate-800 border-t border-slate-700/50 rounded-t-2xl p-5 pb-8 transition-transform duration-300 ${
          leaving ? 'translate-y-full' : 'translate-y-0 animate-slide-up'
        }`}
      >
        {/* Handle */}
        <div className="w-10 h-1 rounded-full bg-slate-600 mx-auto mb-5" />

        <div className="flex flex-col items-center text-center">
          {/* Icon */}
          <div className="w-16 h-16 rounded-full bg-slate-700/50 border-[1.5px] border-slate-600/30 flex items-center justify-center mb-4 text-[32px] leading-none saturate-[0.3] opacity-60">
            <span role="img">{achievement.icon}</span>
          </div>

          <h3 className="text-lg font-bold text-slate-100 mb-1">
            {achievement.title}
          </h3>
          <p className="text-sm text-slate-400 mb-5">
            {achievement.description}
          </p>

          {/* Progress bar */}
          {achievement.target != null && achievement.progress != null && (
            <div className="w-full max-w-[240px] mb-2">
              <div className="flex justify-between text-[10px] text-slate-500 mb-1.5">
                <span>{formatCompact(achievement.progress)} / {formatCompact(achievement.target)}</span>
                <span>{pct}%</span>
              </div>
              <div className="h-2 bg-slate-700/50 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full bg-accent/50 transition-all duration-500"
                  style={{ width: `${Math.max(pct, 2)}%` }}
                />
              </div>
            </div>
          )}

          {/* Hint */}
          {hint && (
            <p className="text-xs text-slate-500 mt-2">{hint}</p>
          )}
        </div>
      </div>
    </div>,
    document.getElementById('app-shell') || document.body
  );
}
